import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Description as DeedIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Lock as LockIcon,
  FolderCopy as CopyIcon,
  ReportProblem as ScamIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ResourceContactForm from '../../components/forms/ResourceContactForm';
import { BUSINESS } from '../../constants/business';

const storageOptions = [
  {
    title: 'Fireproof Home Safe',
    pros: 'Immediate access, one-time cost, also protects other documents',
    cons: 'Must be rated for paper (UL Class 350), can be stolen if not anchored',
    rating: 'Recommended',
  },
  {
    title: 'Bank Safe Deposit Box',
    pros: 'High physical security, protected from house fire or flood',
    cons: 'Annual fee, access limited to bank hours, heirs may need court order to open',
    rating: 'Good',
  },
  {
    title: 'Digital Copies (in addition to paper)',
    pros: 'Free, instantly retrievable, easy to share with your attorney or title agent',
    cons: 'A scan is not a legal substitute for a certified copy; protect the file with encryption',
    rating: 'Always do this too',
  },
  {
    title: 'Desk Drawer or Filing Cabinet',
    pros: 'Convenient',
    cons: 'No protection from fire, water, or theft. Not recommended for original documents.',
    rating: 'Avoid',
  },
];

const deedFacts = [
  {
    question: 'Do I lose my house if I lose my deed?',
    answer:
      'No. Ownership is established by the recorded deed at the county, not by the paper in your hand. Once your deed is recorded with the Middlesex County Clerk (or the clerk of the county where the property sits), the public record is the legal proof of ownership. Losing your copy is an inconvenience, not a loss of ownership.',
  },
  {
    question: 'When will I actually need my deed?',
    answer:
      'Most owners need the deed (or a copy of the recorded deed) when selling or refinancing, applying for certain property tax programs, resolving boundary or estate questions, or transferring the property into a trust. Your title agent or attorney can usually work from the recorded copy.',
  },
  {
    question: 'What other closing documents should I keep with it?',
    answer:
      'Keep your owner\'s title insurance policy, the settlement statement (Closing Disclosure or HUD-1), your survey, and any home warranty documents together with the deed. The owner\'s title policy in particular has no expiration and may be needed decades later.',
  },
  {
    question: 'How do I get a replacement copy in New Jersey?',
    answer:
      'Request a plain or certified copy from the county clerk where the property is located. In Middlesex County, the Clerk\'s office at 75 Bayard Street, New Brunswick provides copies for a small per-page fee, and many counties offer online search portals. You never need to pay a third-party service to get your own deed.',
  },
];

const DeedStorageGuide: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>What Should I Do With My Deed? Storage & Protection Guide | MAD Title Agency</title>
        <meta
          name="description"
          content="How to store and protect your property deed and title documents in New Jersey. Storage options, replacement copies from the Middlesex County Clerk, and deed scams to avoid."
        />
        <meta
          name="keywords"
          content="deed storage, lost deed NJ, replace deed Middlesex County, property deed protection, deed scam letter, Woodbridge NJ deed"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            Deed Storage Guide
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#1e3a8a', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Consumer Guide"
                sx={{ backgroundColor: '#f59e0b', color: 'white', mb: 2 }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}
              >
                What Should I Do With My Deed?
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                How to protect, store, and (if needed) replace your property deed and title documents
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <DeedIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Your Ownership is Recorded
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  The county record protects you, even if the paper is lost
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* What happens after closing */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            What Happens to Your Deed After Closing
          </Typography>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                At closing, the seller signs the deed transferring the property to you. We then send
                that deed to the county clerk for recording. Recording places the transfer in the
                public record, which is what legally establishes and protects your ownership.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                After the county finishes recording (in Middlesex County this typically takes a few
                weeks), the original deed is returned. Depending on the transaction it may come back
                to our office for forwarding or directly to you by mail. That recorded original, with
                the clerk's book and page or instrument number stamped on it, is the copy worth keeping.
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Closed with us and never received your recorded deed?</strong> Call us at{' '}
                  {BUSINESS.contact.phoneDisplay} and we will track down the recording information
                  for you at no charge.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, backgroundColor: '#f0f9ff' }}>
                <LockIcon sx={{ fontSize: 48, color: '#1e3a8a', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Keep These Together
                </Typography>
                <List dense>
                  <ListItem disableGutters>
                    <ListItemIcon><CheckIcon sx={{ color: '#16a34a' }} /></ListItemIcon>
                    <ListItemText primary="Recorded deed" />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon><CheckIcon sx={{ color: '#16a34a' }} /></ListItemIcon>
                    <ListItemText primary="Owner's title insurance policy" />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon><CheckIcon sx={{ color: '#16a34a' }} /></ListItemIcon>
                    <ListItemText primary="Settlement statement" />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon><CheckIcon sx={{ color: '#16a34a' }} /></ListItemIcon>
                    <ListItemText primary="Property survey" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Storage options */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Where to Store Your Deed
          </Typography>
          <Grid container spacing={4}>
            {storageOptions.map((option, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', border: '1px solid #e2e8f0' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {option.title}
                      </Typography>
                      <Chip
                        label={option.rating}
                        size="small"
                        sx={{
                          backgroundColor: option.rating === 'Avoid' ? '#fef2f2' : '#f0fdf4',
                          color: option.rating === 'Avoid' ? '#dc2626' : '#16a34a',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Pros:</strong> {option.pros}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Cons:</strong> {option.cons}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Deed scams */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Deed Scams to Watch For
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, height: '100%', backgroundColor: '#fffbeb' }}>
                <ScamIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  "Deed Copy Service" Letters
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Shortly after closing, many New Jersey homeowners receive official-looking letters
                  offering to send a "certified copy" of their deed for $80 to $110. These mailers are
                  not from the county. The county clerk provides the same copy for a few dollars.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  If you receive one of these letters, you can safely ignore it.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, height: '100%', backgroundColor: '#fef2f2' }}>
                <WarningIcon sx={{ fontSize: 48, color: '#dc2626', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  Deed Theft / Title Fraud
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Criminals occasionally record forged deeds to claim ownership of a property, most
                  often targeting vacant homes, rental properties, and homes owned free and clear.
                  Your owner's title insurance policy is a key protection here.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Several NJ counties, including Middlesex, offer free property alert services that
                  notify you when a document is recorded against your name or property. Enrolling
                  takes a few minutes and is the single best early-warning step.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* FAQ */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Common Questions
          </Typography>
          {deedFacts.map((faq, index) => (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <CopyIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              This guide is general information, not legal advice. For questions about your specific
              deed or title, contact us at{' '}
              <strong>{BUSINESS.contact.email}</strong> or consult a real estate attorney.
            </Typography>
          </Alert>
        </Box>

        {/* Contact Form Section */}
        <ResourceContactForm
          resourceTitle="Deed Storage Guide"
          resourceType="consumer"
          formType="contact"
        />
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Questions About Your Title Documents?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              We help Middlesex County homeowners with deeds, title policies, and recording questions.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Contact Us
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/resources/title-insurance-woodbridge"
                sx={{ borderColor: '#1e3a8a', color: '#1e3a8a', px: 4, py: 1.5, fontSize: '1.1rem' }}
              >
                Title Insurance Guide
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default DeedStorageGuide;
